import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Icon, Lens, Sheet, toast } from '@/design-system'
import { createCatShare, shareUrl } from '@/services/api/shares'
import { isAppError } from '@/services/supabase/client'
import { getCatOfTheDay, type CatOfTheDay } from '@/services/api/cats'
import { useMediaUrl } from '@/hooks/useMediaUrl'
import { useSession } from '@/stores/session'
import { catRefName, formatDateLong } from '@/utils/format'

/**
 * The sentence under the name, chosen by priority and always built from real
 * facts — never invented. (§66)
 */
function phrase(cat: CatOfTheDay): string {
  const name = catRefName(cat.name, cat.catalog_no)
  if (cat.kind === 'birthday') {
    return cat.years === 0
      ? 'Hoy es su día. La portada es suya por derecho.'
      : 'Hoy la portada es suya por derecho. ' +
          (cat.years === 1 ? 'Un año' : cat.years + ' años') +
          ' — felicidades, ' + name + '.'
  }
  if (cat.kind === 'anniversary') {
    return (
      'Tal día como hoy, ' +
      (cat.years === 1 ? 'hace un año, ' : 'hace ' + cat.years + ' años, ') +
      'os conocisteis.' +
      (cat.sighting_count > 1 ? ' ' + cat.sighting_count + ' encuentros después, aquí sigue.' : '')
    )
  }
  if (cat.days_since_seen != null && cat.days_since_seen > 30) {
    return 'Hace ' + cat.days_since_seen + ' días que no lo ves. Lo conociste el ' + formatDateLong(cat.first_met_at) + '.'
  }
  if (cat.days_known < 7) {
    return 'Os conocisteis el ' + formatDateLong(cat.first_met_at) + '. Todo por descubrir.'
  }
  return (
    'Lo conociste el ' +
    formatDateLong(cat.first_met_at) +
    (cat.sighting_count > 1 ? '. ' + cat.sighting_count + ' encuentros.' : '.')
  )
}

/** Round-number days deserve a small celebration in the third line. */
function daysLine(cat: CatOfTheDay): string | null {
  if (cat.household || cat.friend_household) return null
  const bits: string[] = []
  if (cat.place) bits.push('Suele estar por ' + cat.place)
  const d = cat.days_known
  if (d > 0) {
    bits.push(
      d % 100 === 0 || d === 365 || d === 500
        ? '¡' + d + ' días conociéndolo!'
        : 'Lo conoces desde hace ' + d + ' días',
    )
  }
  return bits.length ? bits.join(' · ') : null
}

export function CatOfTheDayCard() {
  const user = useSession((s) => s.user)
  const today = useQuery({
    queryKey: ['cat-of-the-day', user?.id, new Date().toDateString()],
    queryFn: getCatOfTheDay,
    enabled: Boolean(user),
    staleTime: 60 * 60_000,
  })

  const cat = today.data
  const url = useMediaUrl(cat?.cover_media_id, 'full')
  const [enlace, setEnlace] = useState<string | null>(null)
  const [preparando, setPreparando] = useState(false)
  if (!cat) return null
  const nombre = catRefName(cat.name, cat.catalog_no)

  /**
   * Compartir el gato del día (§0084). En dos pasos a propósito: primero se
   * crea el enlace y luego, ya en la hoja, «Enviar» abre el menú del móvil.
   * En el iPhone ese menú tiene que salir de un toque directo, y entre medias
   * hay una llamada a la base.
   */
  async function preparar() {
    if (preparando) return
    setPreparando(true)
    try {
      const { token } = await createCatShare(cat!.cat_id)
      setEnlace(shareUrl(token))
    } catch (e) {
      toast.error(isAppError(e) ? e.message : 'No se ha podido preparar el enlace.')
    } finally {
      setPreparando(false)
    }
  }

  async function enviar() {
    if (!enlace) return
    try {
      if (navigator.share) {
        await navigator.share({ title: nombre, text: 'Mi gato del día en CatGO!: ' + nombre, url: enlace })
        return
      }
      await navigator.clipboard.writeText(enlace)
      toast.success('Enlace copiado')
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') return // cerró el menú sin enviar
      toast.error('No se ha podido compartir. Copia el enlace.')
    }
  }

  async function copiar() {
    if (!enlace) return
    try {
      await navigator.clipboard.writeText(enlace)
      toast.success('Enlace copiado')
    } catch {
      toast.error('No se ha podido copiar.')
    }
  }

  const chip =
    cat.kind === 'birthday' ? (
      <span className="cotd__chip cotd__chip--pink">
        🎂 {cat.years === 0 ? 'Su día' : cat.years === 1 ? 'Cumple 1 año' : 'Cumple ' + cat.years + ' años'}
      </span>
    ) : cat.kind === 'anniversary' ? (
      <span className="cotd__chip cotd__chip--jelly">
        🎉 {cat.years === 1 ? 'Un año juntos' : cat.years + ' años juntos'}
      </span>
    ) : (
      <span className="cotd__chip">
        <Icon name="paw" size={13} /> Gato del día
      </span>
    )

  const extra = daysLine(cat)

  return (
    <div className="cotd-wrap">
      <Link to={'/gato/' + cat.cat_id} className="cotd glass">
        <div className="cotd__photo">
          {url ? (
            <img src={url} alt="" loading="lazy" decoding="async" />
          ) : (
            <span className="cotd__empty">
              <Icon name="cat" size={44} />
            </span>
          )}
          {chip}
        </div>
        <div className="cotd__body">
          <h2 className="cotd__name">{catRefName(cat.name, cat.catalog_no)}</h2>
          <p className="cotd__phrase">{phrase(cat)}</p>
          {extra && (
            <p className="cotd__meta">
              <Icon name="pin" size={12} /> {extra}
            </p>
          )}
        </div>
      </Link>

      <button
        type="button"
        className="icon-btn cotd__share"
        aria-label={'Compartir a ' + nombre}
        onClick={() => void preparar()}
      >
        {preparando ? <Lens size={18} /> : <Icon name="share" size={20} />}
      </button>

      <Sheet open={enlace !== null} onClose={() => setEnlace(null)} title="Compartir el gato del día">
        <div className="stack stack--4">
          <p className="u-body-sm u-secondary">
            Quien abra el enlace verá una foto tuya de {nombre}, el nombre que le pusiste y tu nombre.
            Nunca dónde vive ni cuándo lo viste. El enlace dura 30 días.
          </p>
          <Button block size="lg" icon="share" onClick={() => void enviar()}>
            Enviar
          </Button>
          <Button block variant="secondary" icon="link" onClick={() => void copiar()}>
            Copiar enlace
          </Button>
        </div>
      </Sheet>
    </div>
  )
}
